import { createContext, useContext } from 'react';

import type {
    ExtendedMaterialOnTheFly,
    ExtendedMaterialSubcategory,
    MaterialOnTheFly,
    MaterialSubcategory,
} from '../../gql';
import type { MaterialOnTheFlyValue } from '../../types/MaterialOnTheFlyValue';

type MaterialsOnTheFlyState<
    T extends ExtendedMaterialOnTheFly | MaterialOnTheFly,
> = Array<T>;

type MaterialsOnTheFlyAction<
    T extends ExtendedMaterialOnTheFly | MaterialOnTheFly,
> =
    | {
          type: 'create';
          payload: {
              materialOnTheFlyValue: MaterialOnTheFlyValue;
              materialSubcategory: T['materialSubcategory'];
          };
      }
    | {
          type: 'update';
          payload: {
              materialOnTheFlyValue: MaterialOnTheFlyValue;
              materialSubcategory: T['materialSubcategory'];
          };
      };

const convertToMaterialOnTheFly = (
    newValue: MaterialOnTheFlyValue,
    materialSubcategory: MaterialSubcategory
): MaterialOnTheFly => {
    if (!newValue.materialOnTheFlyPublicId)
        throw new Error(
            'A public id is required for materials on the fly for bom'
        );

    const newMaterialOnTheFly = {
        materialOnTheFlyPublicId: newValue.materialOnTheFlyPublicId,
        materialSubcategory,
        compositions: newValue.compositions.map((composition) => ({
            ...composition,
            rawMaterial: {
                rawMaterialPublicId:
                    composition.rawMaterial?.rawMaterialPublicId ?? '',
                rawMaterialName: composition.rawMaterial?.rawMaterialName ?? '',
                isDeleted: false,
            },
            percentage: composition.percentage ?? 0,
        })),
        weightValue: newValue.weightValue ?? null,
        weightUnit: newValue.weightUnit,
        weightMeasure: newValue.weightMeasure,
        usagePublicId: newValue.usagePublicId, // Frontend-only property
        // Frontend-only property, only kept for MOFs that were loaded from the backend
        ...(newValue.isExistingMof && { isExistingMof: true }),
        // BOM MOFs can optionally have a manufacture detail
        manufactureDetail: newValue.manufactureDetail ?? null,
    } as MaterialOnTheFly;

    return newMaterialOnTheFly;
};

const convertToExtendedMaterialOnTheFly = (
    newValue: MaterialOnTheFlyValue,
    materialSubcategory: ExtendedMaterialSubcategory
): ExtendedMaterialOnTheFly => {
    if (!newValue.materialOnTheFlyPublicId)
        throw new Error(
            'A public id is required for materials on the fly for extended composition'
        );
    if (!newValue.compositions.length)
        throw new Error(
            'At least one composition is required for materials on the fly for extended composition'
        );
    if (!newValue.manufactureDetail)
        throw new Error(
            'Manufacture detail is required for materials on the fly for extended composition'
        );

    const newMaterialOnTheFly: ExtendedMaterialOnTheFly = {
        materialOnTheFlyPublicId: newValue.materialOnTheFlyPublicId,
        materialSubcategory,
        compositions: newValue.compositions.map((composition) => ({
            ...composition,
            feId: composition.feId ?? crypto.randomUUID(), // Ensure `feId` is present
            rawMaterial: {
                rawMaterialPublicId:
                    composition.rawMaterial?.rawMaterialPublicId ?? '',
                rawMaterialName: composition.rawMaterial?.rawMaterialName ?? '',
                isDeleted: false,
            },
            percentage: composition.percentage ?? 0,
        })),
        manufactureDetail: newValue.manufactureDetail, // Include manufactureDetail for extended compositions
    };

    return newMaterialOnTheFly;
};

const materialsOnTheFlyReducer = <
    T extends ExtendedMaterialOnTheFly | MaterialOnTheFly,
>(
    state: MaterialsOnTheFlyState<T>,
    action: MaterialsOnTheFlyAction<T>
): MaterialsOnTheFlyState<T> => {
    if (action.type === 'create') {
        const newMaterial: T = !(
            'isOnTheFlySupported' in action.payload.materialSubcategory
        )
            ? (convertToExtendedMaterialOnTheFly(
                  action.payload.materialOnTheFlyValue,
                  action.payload.materialSubcategory
              ) as T)
            : (convertToMaterialOnTheFly(
                  action.payload.materialOnTheFlyValue,
                  action.payload.materialSubcategory
              ) as T);

        // We concat the new mof to the end of our state.
        return state.concat(newMaterial);
        // Disabling this eslint rule for readability
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    } else if (action.type === 'update') {
        const updatedMaterialOnTheFly: T = !(
            //only bomssubcategories have isOnTheFlySupported
            ('isOnTheFlySupported' in action.payload.materialSubcategory)
        )
            ? (convertToExtendedMaterialOnTheFly(
                  action.payload.materialOnTheFlyValue,
                  action.payload.materialSubcategory
              ) as T)
            : (convertToMaterialOnTheFly(
                  action.payload.materialOnTheFlyValue,
                  action.payload.materialSubcategory
              ) as T);

        // it replaces the existing material on the fly with the updated one.
        return state.map((mof) =>
            mof.materialOnTheFlyPublicId ===
            updatedMaterialOnTheFly.materialOnTheFlyPublicId
                ? updatedMaterialOnTheFly
                : mof
        );
    } else {
        return state;
    }
};

interface MaterialsOnTheFlyContextType<
    T extends ExtendedMaterialOnTheFly | MaterialOnTheFly,
> {
    materialsOnTheFly: Array<T>;
    createMaterialOnTheFly: (
        materialOnTheFlyValue: MaterialOnTheFlyValue,
        materialSubcategory: T['materialSubcategory']
    ) => void;
}

const MaterialsOnTheFlyContext = createContext<MaterialsOnTheFlyContextType<
    ExtendedMaterialOnTheFly | MaterialOnTheFly
> | null>(null);

const useMaterialsOnTheFly = () => {
    const context = useContext(MaterialsOnTheFlyContext);
    if (!context)
        throw new Error(
            'useMaterialsOnTheFly must be used within a MaterialsOnTheFlyProvider'
        );
    return context;
};

export {
    convertToExtendedMaterialOnTheFly,
    convertToMaterialOnTheFly,
    MaterialsOnTheFlyContext,
    materialsOnTheFlyReducer,
    useMaterialsOnTheFly,
};
export type { MaterialsOnTheFlyContextType, MaterialsOnTheFlyState };
